import { Heading } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContractRenewalUi,
  ContractRenewalUiSchema,
  CrmOpportunityForInput,
  IAccountRespSchema,
  IBillingSchedulePeriodsSchema,
  IContract,
  IContractRenewalReqSchema,
  IOpportunitySummary,
  IQuoteRespSchema,
  NewQuoteTypeEnum,
  ProductInclusionEnum,
  ProductTypeEnum,
  QuoteAdditionalFields,
  SubscriptionTimingEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { toDateOnly, toDateShort } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { clamp } from 'date-fns/clamp';
import { format } from 'date-fns/format';
import { formatISO } from 'date-fns/formatISO';
import { isAfter } from 'date-fns/isAfter';
import { parseISO } from 'date-fns/parseISO';
import { subDays } from 'date-fns/subDays';
import isString from 'lodash/isString';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleApiErrorToast } from '../../api/axios';
import {
  doRenewContract,
  useGetOrCreateOpportunityWithCrmLink,
} from '../../api/cpqService';
import { useGetById } from '../../api/queryUtils';
import { getQuoteEditRoute } from '../../constants/routes';
import { useBillingSchedule } from '../../hooks/useBillingSchedule';
import {
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MRadio,
  MRadioGroup,
  MSkeleton,
  MStack,
  MText,
} from '../Monetize';
import { DatePicker } from '../Monetize/DatePicker/DatePicker';
import { ContractLengthPopover } from './ContractLengthPopover';

interface RenewalModalProps {
  contract: IContract;
  crmOpportunity?: CrmOpportunityForInput | null;
  existingOpportunityId?: Maybe<string>;
  account?: IAccountRespSchema | '';
  onClose: () => void;
  additionalFields?: QuoteAdditionalFields;
}

export const RenewalModal = ({
  contract,
  crmOpportunity,
  existingOpportunityId,
  account,
  onClose,
  additionalFields,
}: RenewalModalProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: doCreateOrGetOpportunity } =
    useGetOrCreateOpportunityWithCrmLink();

  const { data: quote, isFetching } = useGetById<IQuoteRespSchema>(
    'cpqServiceQuotes',
    contract.latestQuoteId,
    {
      enabled: !!contract.latestQuoteId,
      retry: false,
      refetchOnWindowFocus: false,
      meta: { showErrorToast: true },
    },
  );

  const hasUsageOrArrears = useMemo(() => {
    return (
      quote?.quoteOfferings?.some(
        (offering) =>
          offering?.subscriptionTiming === SubscriptionTimingEnum.ARREARS ||
          offering.items.some(
            (item) => item.productType === ProductTypeEnum.USAGE,
          ),
      ) ?? false
    );
  }, [quote?.quoteOfferings]);

  const { loading: billingScheduleLoading, quoteBillingSchedule } =
    useBillingSchedule(quote);

  const alignDateWithMonthlySchedule = (
    initialDate: Date,
    schedule: IBillingSchedulePeriodsSchema,
  ): Date => {
    const ymKey = format(initialDate, 'yyyy-MM');
    const matchedPeriod = schedule.find((period) =>
      period.startDate.startsWith(ymKey),
    );

    return matchedPeriod ? parseISO(matchedPeriod.startDate) : initialDate;
  };

  const getInitialRenewalDate = useMemo((): string => {
    const start = parseISO(contract.startDate);
    const end = parseISO(contract.endDate);
    const today = new Date();

    const earliest = isAfter(today, start) ? addDays(start, 1) : start;

    const latest = subDays(end, 1); // day before end
    const chosen = clamp(today, { start: earliest, end: latest });

    if (quoteBillingSchedule?.periods.length && hasUsageOrArrears) {
      const newInitDate = alignDateWithMonthlySchedule(
        chosen,
        quoteBillingSchedule.periods,
      );
      return formatISO(newInitDate, { representation: 'date' });
    }

    return formatISO(chosen, { representation: 'date' });
  }, [
    contract.endDate,
    contract.startDate,
    hasUsageOrArrears,
    quoteBillingSchedule?.periods,
  ]);

  const {
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ContractRenewalUi>({
    resolver: zodResolver(ContractRenewalUiSchema),
    mode: 'onChange',
    defaultValues: {
      newQuoteType: NewQuoteTypeEnum.NET_NEW,
      renewalDate: getInitialRenewalDate,
      productInclusion: ProductInclusionEnum.AS_OF_RENEWAL_DATE,
    },
  });

  useEffect(() => {
    if (!billingScheduleLoading && quoteBillingSchedule?.periods.length) {
      setValue('renewalDate', getInitialRenewalDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [billingScheduleLoading, quoteBillingSchedule?.periods.length]);

  const watchNewQuoteType = watch('newQuoteType');
  const watchRenewalDateStr = watch('renewalDate');

  const watchRenewalDate = useMemo(
    () => toDateShort(watchRenewalDateStr ?? new Date()),
    [watchRenewalDateStr],
  );
  const contractEndDate = toDateShort(contract.endDate);

  const handleRenewContract = async () => {
    setIsLoading(true);
    try {
      let payload: {
        contractRenewalOptions?: IContractRenewalReqSchema;
        opportunity?: Partial<
          Pick<IOpportunitySummary, 'id' | 'name' | 'customId'>
        >;
      } = {};

      let opportunityId = existingOpportunityId;

      const { newQuoteType, renewalDate, productInclusion, contractLength } =
        getValues();

      if (crmOpportunity && account && !isString(account)) {
        const opportunity = await doCreateOrGetOpportunity({
          customId: crmOpportunity.id,
          createPayload: {
            accountId: account.id,
            name: crmOpportunity.name,
            customId: crmOpportunity.id,
          },
        });
        opportunityId = opportunity.id;
      }

      if (newQuoteType === NewQuoteTypeEnum.EARLY_RENEWAL) {
        payload = {
          contractRenewalOptions: {
            newQuoteType,
            renewalDate: renewalDate ? toDateOnly(renewalDate) : undefined,
            includeSubscriptionsAsOfRenewalDate:
              productInclusion === ProductInclusionEnum.AS_OF_RENEWAL_DATE,
            contractLength,
          },
        };
      }

      if (opportunityId) {
        payload.opportunity = opportunityId ? { id: opportunityId } : undefined;
      }

      payload.contractRenewalOptions = {
        ...payload.contractRenewalOptions,
        ...additionalFields,
      };

      const renewedQuote = await doRenewContract(contract.id, payload);
      renewedQuote?.id &&
        navigate({
          pathname: getQuoteEditRoute(renewedQuote.id),
        });
    } catch (error) {
      handleApiErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRenewalMinDate = useMemo(() => {
    if (quoteBillingSchedule?.periods.length && hasUsageOrArrears) {
      return parseISO(quoteBillingSchedule.periods[0].startDate);
    }

    const start = parseISO(contract.startDate);
    const today = new Date();

    return isAfter(today, start) ? addDays(start, 1) : start;
  }, [contract.startDate, hasUsageOrArrears, quoteBillingSchedule?.periods]);

  const handleDateSelection = (date: string | null) => {
    let selectedDate = date ?? undefined;

    if (quoteBillingSchedule?.periods.length && hasUsageOrArrears && date) {
      const newInitDate = alignDateWithMonthlySchedule(
        parseISO(date),
        quoteBillingSchedule.periods,
      );

      selectedDate = formatISO(newInitDate, { representation: 'date' });
    }

    setValue('renewalDate', selectedDate, { shouldDirty: true });

    ensureEarlyRenewalQuoteType();
  };

  const ensureEarlyRenewalQuoteType = () => {
    if (getValues('newQuoteType') !== NewQuoteTypeEnum.EARLY_RENEWAL) {
      setValue('newQuoteType', NewQuoteTypeEnum.EARLY_RENEWAL);
    }
  };

  return (
    <MCenterModal
      modalHeaderProps={{ px: 6, py: 4 }}
      modalBodyProps={{ px: 6, py: 4 }}
      renderModalTitleActions={() => (
        <Heading fontSize="18px" color="tPurple.base">
          Renew Contract
        </Heading>
      )}
      isOpen
      size="lg"
      onClose={onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton onClick={onClose} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            type="submit"
            minW="auto"
            isLoading={isLoading}
            onClick={handleRenewContract}
          >
            Next
          </MButton>
        </MStack>
      )}
    >
      <MGrid gap={4} templateColumns="1">
        <MGridItem>
          <MText color="tGray.darkGrayPurple">
            This contract term is {''}
            {toDateShort(contract.startDate)} {''}
            to {''}
            {contractEndDate}.
          </MText>
        </MGridItem>

        <MGridItem>
          <MGrid templateColumns="0.08fr 1fr">
            <MGridItem>
              <MText>On:</MText>
            </MGridItem>
            <MGridItem>
              <MFormField error={errors?.newQuoteType} mb={2} mt={0.5}>
                <Controller
                  name="newQuoteType"
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <MRadioGroup
                      onChange={(value) => {
                        onChange(value);
                        if (value === NewQuoteTypeEnum.EARLY_RENEWAL) {
                          setValue(
                            'productInclusion',
                            ProductInclusionEnum.AS_OF_RENEWAL_DATE,
                          );
                        }
                      }}
                      {...rest}
                    >
                      <MStack direction="column">
                        <MRadio value={NewQuoteTypeEnum.NET_NEW}>
                          End of Contract Term - After {contractEndDate}
                        </MRadio>
                        <MRadio value={NewQuoteTypeEnum.EARLY_RENEWAL}>
                          Early Renewal Date - Before {contractEndDate}
                        </MRadio>
                      </MStack>
                    </MRadioGroup>
                  )}
                />
              </MFormField>
            </MGridItem>
          </MGrid>
        </MGridItem>

        {watchNewQuoteType === NewQuoteTypeEnum.EARLY_RENEWAL &&
          billingScheduleLoading &&
          isFetching && <SkeletonLoader />}
        {watchNewQuoteType === NewQuoteTypeEnum.EARLY_RENEWAL &&
          !isFetching && (
            <MGridItem>
              <MText fontWeight={600}>Early Renewal Details</MText>

              <MGrid templateColumns="0.35fr 0.6fr" alignItems="center">
                <MGridItem>
                  <MText>Renewal Start Date:</MText>
                </MGridItem>
                <MGridItem>
                  <MFormField error={errors?.renewalDate}>
                    <Controller
                      name="renewalDate"
                      control={control}
                      render={({ field }) => (
                        <MBox maxWidth="10rem">
                          <DatePicker
                            minDate={getRenewalMinDate}
                            maxDate={addDays(parseISO(contract.endDate), -1)}
                            mode={hasUsageOrArrears ? 'MONTH' : 'DATE'}
                            {...field}
                            onChange={handleDateSelection}
                          />
                        </MBox>
                      )}
                    />
                  </MFormField>
                </MGridItem>
              </MGrid>

              <MFlex gap="2" align="center">
                <MText>Renewal Length: </MText>
                <ContractLengthPopover
                  contractLength={12}
                  isCustomContractLength
                  handleValueUpdate={(value) =>
                    setValue('contractLength', value)
                  }
                />
                <MText fontWeight="bold">Months</MText>
              </MFlex>

              <MFormField error={errors?.productInclusion} mb={2} mt={0.5}>
                <MText fontWeight={600} mb={2}>
                  Include Products as of:
                </MText>
                <Controller
                  name="productInclusion"
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <MRadioGroup
                      ml="5"
                      value={value}
                      onChange={(val) => onChange(val)}
                      {...rest}
                    >
                      <MStack direction="column">
                        <MRadio value={ProductInclusionEnum.AS_OF_RENEWAL_DATE}>
                          Early Renewal Date ({watchRenewalDate})
                        </MRadio>
                        <MRadio
                          value={ProductInclusionEnum.END_OF_CONTRACT_DATE}
                        >
                          End of Current Contract ({contractEndDate})
                        </MRadio>
                      </MStack>
                    </MRadioGroup>
                  )}
                />
              </MFormField>

              <MText color="tGray.darkGrayPurple">
                This renews the contract on {watchRenewalDate} and ends the
                existing contract the day before this date.
              </MText>
            </MGridItem>
          )}
      </MGrid>
    </MCenterModal>
  );
};

const SkeletonLoader = () => {
  return Array.from({ length: 5 }, (_, index) => (
    <MSkeleton key={index} height="1rem" width="100%" mt={2} />
  ));
};
